// Module: instrumentClassCapabilityGate.js
// Purpose: Class-keyed (broker, instrument-class), FAIL-CLOSED order-form capability gate
//          (order-instrument-normalization R6.1.1/R6.1.2/R6.2/R6.3/R6.4). Consumed identically by
//          SmartOrderForm (manual, single-account) and TradeExecutionStep (algo builder,
//          N-account intersection) — the class-keyed analogue of ../utils/brokerCapabilities.js.
//
//          brokerCapabilities.js's broker-only product/order-type gate (the fail-OPEN sibling this
//          module superseded) was REMOVED in loop-back-4 once it had zero production callers;
//          brokerCapabilities.js now retains only buildBrokerMaps/extractBrokerList (broker-list
//          normalization). This module is the live, opposite-polarity gate: every axis helper here
//          defaults CLOSED (`false`) on a
//          missing/absent manifest block, per R6.1.2 (arch-data-lineage DEFECT-2 close).
//
//          Data source: GET /api/v1/brokers/{account}/capabilities (via gateway only — never the
//          adapter/BIS host:port directly), relayed byte-for-byte from each adapter's
//          order_manifest.py (backend/shared/order_taxonomy, 02-design.md §3.3/§3.5).

import { services } from '../services/api';

/*>>> CONSTANTS <<<*/

//! Sentinel class key — the manifest served by any broker is NEVER keyed "UNKNOWN" (adapters only
//! ever declare real "{TYPE}:{SEGMENT}" blocks, e.g. "EQUITY:NSE_BSE"). Resolving to this sentinel
//! guarantees `manifestByBrokerAndClass[broker][UNKNOWN_CLASS_KEY]` is always undefined, so
//! buildClassKeyedCapabilityGate falls through to the SAME "no manifest block" fail-closed path
//! as every other unresolved class — every control disabled, never a wrong-but-populated one.
export const UNKNOWN_CLASS_KEY = 'UNKNOWN';

//* R1.1/R3.3 — the six canonical `order_taxonomy.enums.InstrumentType` members
//  (backend/shared/instrument/schema.py:20-27, re-exported by order_taxonomy per 02-design.md
//  §3.1). An `instrument_type` outside this set is treated as unrecognized -> UNKNOWN, the exact
//  DEFECT-2 fail-closed behavior (never silently substituting 'EQUITY').
const KNOWN_INSTRUMENT_TYPES = new Set(['EQUITY', 'FUTURE', 'OPTION', 'COMMODITY', 'CURRENCY', 'INDEX']);

//--> R3.3 — the 4-line exchange-segment bucket table, PORTED VERBATIM from the server's own
//    `backend/shared/order_taxonomy/class_key.py::_EXCHANGE_TO_SEGMENT` (DO NOT re-derive
//    independently here — a client/server mismatch on this table is the exact B7 drift disease
//    this whole feature exists to kill; verify against a shared fixture, per R1.4-style parity).
const EXCHANGE_TO_SEGMENT = {
  NSE: 'NSE_BSE',
  BSE: 'NSE_BSE',
  NFO: 'NFO_BFO',
  BFO: 'NFO_BFO',
  MCX: 'MCX',
  CDS: 'CDS',
  BCD: 'CDS',
};

/*>>> CLASS-KEY RESOLUTION (DEFECT-2 close) <<<*/

/**
 * resolveInstrumentClassKey — resolves an instrument's (instrument_type, exchange) pair to the
 * manifest's class key, e.g. "EQUITY:NSE_BSE", "FUTURE:MCX".
 *
 * MUST fail CLOSED to UNKNOWN_CLASS_KEY on a missing/blank/unrecognized `instrument_type` OR
 * `exchange` — this is a NEW, gate-only resolver, NOT a call into the existing `|| 'EQUITY'`
 * fallback sites (SmartOrderForm.jsx:177, WatchList.jsx:634, WatchListRow.jsx:138,
 * StrategyBuilder.jsx:1630 — those stay exactly as-is for their own non-gating display purposes,
 * out of this feature's scope). Substituting 'EQUITY' here would silently mis-key an F&O
 * instrument with a missing/unrecognized `instrument_type` into the EQUITY manifest block,
 * enabling the wrong product/order-type/variety/TIF set — precisely the failure R6.1.2's
 * fail-closed AC exists to prevent (arch-data-lineage DEFECT-2).
 *
 * `manifestByBrokerAndClass` will never carry a block keyed "UNKNOWN", so
 * `buildClassKeyedCapabilityGate` (below) falls through to the SAME "no manifest block"
 * fail-closed path as every other unresolved class.
 *
 * @param {{instrument_type?: string, exchange?: string}|null|undefined} instrument
 * @returns {string} the resolved class key, or UNKNOWN_CLASS_KEY on missing/blank/unrecognized input.
 */
export const resolveInstrumentClassKey = (instrument) => {
  const rawType = instrument?.instrument_type;
  if (typeof rawType !== 'string' || !rawType.trim()) return UNKNOWN_CLASS_KEY;
  const instrumentType = rawType.trim().toUpperCase();
  if (!KNOWN_INSTRUMENT_TYPES.has(instrumentType)) return UNKNOWN_CLASS_KEY;

  const rawExchange = instrument?.exchange;
  if (typeof rawExchange !== 'string' || !rawExchange.trim()) return UNKNOWN_CLASS_KEY;
  const exchange = rawExchange.trim().toUpperCase();
  //--> Unrecognized (non-4-line-table) exchange still yields a deterministic bucket (the raw
  //    uppercased string) rather than UNKNOWN — this mirrors the server's own fail-visible, not
  //    fail-silent, fallback (class_key.py::_segment_for_exchange). It will simply never match a
  //    served manifest block (adapters only ever declare NSE_BSE/NFO_BFO/MCX/CDS classes, R3.3),
  //    so it still fails closed at the lookup layer below, not by fabricating a wrong sentinel
  //    for what may be a legitimate (if narrow/unmapped) exchange segment.
  const segment = EXCHANGE_TO_SEGMENT[exchange] || exchange;

  return `${instrumentType}:${segment}`;
};

/*>>> FAIL-CLOSED AXIS HELPERS (R6.1.2 — inverts brokerCapabilities.js:42,44,50's `return true`) <<<*/

//--> A per-value manifest entry ("WireValue") is `{wire_code, broker_label, definition}`
//    (02-design.md §3.3, widened post-arch-data-lineage DEFECT-1). `wire_code == null` covers
//    BOTH shapes R4.1/R4.2 distinguish server-side (key absent entirely vs key present but
//    unmapped) — either way the FE control must be disabled, never permitted.
const _isAllowed = (manifestBlock, axis, canonicalValue) => {
  const entry = manifestBlock?.[axis]?.[canonicalValue];
  return Boolean(entry && entry.wire_code != null);
};

/** isProductAllowedForClass — false (fail-closed) when manifestBlock is absent/undefined/null. */
export const isProductAllowedForClass = (manifestBlock, canonicalProduct) =>
  _isAllowed(manifestBlock, 'products', canonicalProduct);

/** isOrderTypeAllowedForClass — false (fail-closed) when manifestBlock is absent/undefined/null. */
export const isOrderTypeAllowedForClass = (manifestBlock, canonicalOrderType) =>
  _isAllowed(manifestBlock, 'order_types', canonicalOrderType);

/** isVarietyAllowedForClass — false (fail-closed) when manifestBlock is absent/undefined/null. */
export const isVarietyAllowedForClass = (manifestBlock, canonicalVariety) =>
  _isAllowed(manifestBlock, 'varieties', canonicalVariety);

/** isTifAllowedForClass — false (fail-closed) when manifestBlock is absent/undefined/null. */
export const isTifAllowedForClass = (manifestBlock, canonicalTif) =>
  _isAllowed(manifestBlock, 'tif', canonicalTif);

/*>>> TOOLTIP + BROKER-LABEL (R6.3/R6.4) <<<*/

/**
 * getDefinitionForClass — R6.3: the ⓘ tooltip text, read directly off the fetched capability
 * wire's `definition` field — the ONLY legal source (the frontend cannot import the Python
 * `order_taxonomy` package, backend-only). Returns '' (never a hardcoded fallback) when the wire
 * carries no definition for this value — an empty tooltip is the honest rendering of "the server
 * didn't send one," not a component-local guess (arch-data-lineage DEFECT-1 close).
 */
export const getDefinitionForClass = (manifestBlock, axis, canonicalValue) =>
  manifestBlock?.[axis]?.[canonicalValue]?.definition || '';

/**
 * getBrokerLabelForClass — R6.4: the "you know this as ___" hint, read directly off the fetched
 * manifest's `broker_label` field. Same no-hardcoded-fallback discipline as the tooltip.
 */
export const getBrokerLabelForClass = (manifestBlock, axis, canonicalValue) =>
  manifestBlock?.[axis]?.[canonicalValue]?.broker_label || '';

/*>>> GATE BUILDER (R6.1.1/R6.2) <<<*/

/**
 * buildClassKeyedCapabilityGate — the class-keyed analogue of brokerCapabilities.js's
 * `buildCapabilityGate` (brokerCapabilities.js:64-74), consumed identically by both
 * SmartOrderForm.jsx and TradeExecutionStep.jsx. For N selected accounts (algo fan-out places the
 * same order on every selected account) an option is allowed only if EVERY selected account's
 * resolved (broker, instrumentClass) manifest block allows it — same `.every(...)` intersection
 * semantics as the broker-only gate, now class-keyed. Unlike the legacy gate, an account whose
 * (broker, class) block is missing fails THAT AXIS CLOSED for the whole gate (R6.1.2) — it never
 * imposes "no restriction".
 *
 * Also exposes `getDefinition`/`getBrokerLabel` convenience readers (R6.3/R6.4) bound to the
 * FIRST selected account's resolved block — the definition text is byte-identical across brokers
 * for the same canonical value (contract-tested server-side, 02-design.md §3.3), so any selected
 * account's block is a valid representative source; `broker_label` is genuinely per-broker, so
 * for a multi-broker selection (TradeExecutionStep) this renders one representative label, not a
 * per-broker breakdown — there is no dedicated multi-broker label UI surface requested by R6.4.
 *
 * @param {Array} accounts                    broker accounts ({account_id, broker_id, ...})
 * @param {Array} selectedIds                 selected account_ids
 * @param {Object} manifestByBrokerAndClass   broker_id -> classKey -> ClassBlock (fetched
 *                                             manifests — see fetchCapabilitiesForAccounts below)
 * @param {string} instrumentClass            resolveInstrumentClassKey(...) output
 */
export const buildClassKeyedCapabilityGate = (accounts, selectedIds, manifestByBrokerAndClass, instrumentClass) => {
  const blocks = (selectedIds || [])
    .map((id) => (accounts || []).find((a) => a.account_id === id))
    .filter(Boolean)
    .map((a) => {
      const byClass = manifestByBrokerAndClass?.[a.broker_id] ?? manifestByBrokerAndClass?.[String(a.broker_id)] ?? null;
      //! Fail-closed on a fetch failure/timeout/non-200 for this account: the caller omits that
      //! broker's key from manifestByBrokerAndClass entirely (see fetchCapabilitiesForAccounts),
      //! so `byClass` resolves to null here -> every axis helper above returns false for it.
      return byClass?.[instrumentClass] ?? null;
    });

  //! No selected account at all -> a vacuous `.every()` on an empty array would return `true`
  //! (permit) for every option, which is NOT a valid "everything allowed" state for a gate whose
  //! default must be closed (R6.1.2) — nothing selected means nothing should render as usable.
  if (blocks.length === 0) {
    return {
      productAllowed: () => false,
      orderTypeAllowed: () => false,
      varietyAllowed: () => false,
      tifAllowed: () => false,
      getDefinition: () => '',
      getBrokerLabel: () => '',
    };
  }

  return {
    productAllowed: (canonicalProduct) => blocks.every((b) => isProductAllowedForClass(b, canonicalProduct)),
    orderTypeAllowed: (canonicalOrderType) => blocks.every((b) => isOrderTypeAllowedForClass(b, canonicalOrderType)),
    varietyAllowed: (canonicalVariety) => blocks.every((b) => isVarietyAllowedForClass(b, canonicalVariety)),
    tifAllowed: (canonicalTif) => blocks.every((b) => isTifAllowedForClass(b, canonicalTif)),
    getDefinition: (axis, canonicalValue) => getDefinitionForClass(blocks[0], axis, canonicalValue),
    getBrokerLabel: (axis, canonicalValue) => getBrokerLabelForClass(blocks[0], axis, canonicalValue),
  };
};

/*>>> DATA FETCH (R5.2.1/R6.1.2 — shared by both consuming surfaces) <<<*/

/**
 * fetchCapabilitiesForAccounts — per-broker capability fetch + aggregation, shared by both
 * SmartOrderForm (single-account) and TradeExecutionStep (N-account) so the fail-closed-on-fetch-
 * failure behavior (R6.1.2) lives in exactly one place, not duplicated per surface.
 *
 * Calls ONLY `services.brokerAccounts.getBrokerCapabilities` (api.js -> gateway -> BIS ->
 * adapter S2S) — never a microservice/adapter host:port directly.
 *
 * Fetches are de-duplicated per `broker_id` (the manifest is identical for every account on the
 * same broker — the BIS relay itself caches this way, 02-design.md §3.4). On ANY fetch
 * failure/timeout/non-200 for a given broker, that broker's key is simply OMITTED from the
 * returned `manifests` map — never populated with a stale/empty-but-truthy placeholder that could
 * be misread as "no restriction". `buildClassKeyedCapabilityGate` treats an omitted broker key
 * exactly like a genuinely-absent (broker, class) block — fails closed, never the legacy permit.
 *
 * Defect B (#28) B3 — the return is an ADDITIVE envelope `{ manifests, status }`:
 *   - `manifests` is the UNCHANGED broker_id -> classKey -> ClassBlock map (every existing
 *     `buildClassKeyedCapabilityGate` consumer keeps reading this field exactly as before — the
 *     fail-closed safety net is untouched).
 *   - `status` is a NEW sibling `{ [brokerId]: 'ok' | 'error' }` map — 'error' on a thrown
 *     fetch/timeout/non-200 AND on an empty/malformed payload (no `classes` map); 'ok' on
 *     success. Before this, the fetch-failure signal was DESTROYED at this exact boundary
 *     (console.log only, no per-broker record) — TradeExecutionStep and SmartOrderForm's
 *     `UnsupportedAccountPanel`/inline messaging could not tell "this broker's fetch failed"
 *     (State A) from "this broker genuinely doesn't support this instrument class" (State C).
 *     `resolveAccountGateState` below is the ONE place both surfaces resolve that distinction
 *     from this `status` map.
 *
 * @param {Array} accounts     broker accounts ({account_id, broker_id})
 * @param {Array} selectedIds  selected account_ids
 * @returns {Promise<{manifests: Object, status: Object}>}  never throws
 */
export const fetchCapabilitiesForAccounts = async (accounts, selectedIds) => {
  const selectedAccountObjs = (selectedIds || [])
    .map((id) => (accounts || []).find((a) => a.account_id === id))
    .filter(Boolean);

  //--> broker_id -> a representative account_id to fetch through (any selected account on that
  //    broker works — the manifest is per-broker, not per-account, R5.2.1).
  const brokerToAccountId = new Map();
  selectedAccountObjs.forEach((a) => {
    if (!brokerToAccountId.has(a.broker_id)) brokerToAccountId.set(a.broker_id, a.account_id);
  });

  const manifests = {};
  const status = {};
  await Promise.all(
    Array.from(brokerToAccountId.entries()).map(async ([brokerId, accountId]) => {
      try {
        const res = await services.brokerAccounts.getBrokerCapabilities(accountId);
        //--> Envelope-tolerant unwrap — mirrors the existing `/modify-capabilities` consumer
        //    pattern (ModifyOrderModal.jsx:123: `capRes?.data?.data ?? capRes?.data ?? null`).
        const payload = res?.data?.data ?? res?.data ?? null;
        const classes = payload?.classes;
        if (classes && typeof classes === 'object') {
          manifests[brokerId] = classes;
          status[brokerId] = 'ok';
        } else {
          //--> Empty/malformed payload (no `classes` map) -> broker key stays OMITTED from
          //    `manifests` (fails closed via buildClassKeyedCapabilityGate, unchanged) AND is
          //    recorded 'error' in `status` (B3) so the DISPLAY layer can tell this apart from a
          //    genuine per-class capability gap.
          status[brokerId] = 'error';
        }
      } catch (err) {
        //! RC3 — was `console.log`: a MONEY-PATH capability fetch that silently degrades a
        //! broker to fail-closed must not sit at a routinely-filtered log level
        //! (feedback_logs_first_validation). `[DIAG]` prefix kept (feedback_diag_prefix).
        console.warn('[DIAG] broker capability manifest fetch failed (fail-closed, no legacy permit)', {
          brokerId,
          accountId,
          error: err?.message,
        });
        //--> fetch failure/timeout/non-200 -> broker key stays OMITTED from `manifests` (fails
        //    closed) AND recorded 'error' in `status` (B3).
        status[brokerId] = 'error';
      }
    })
  );
  return { manifests, status };
};

/*>>> STATE RESOLUTION (Defect B #28 — the shared 5-state resolver) <<<*/

/**
 * resolveAccountGateState — the ONE place both SmartOrderForm and TradeExecutionStep distinguish
 * the 3 negative states Defect B's root cause collapses into a single fail-closed boolean:
 *   - a genuine per-(broker, instrument-class) capability gap (State C, "the broker really can't
 *     trade this"),
 *   - a broker-capability fetch failure/timeout (State A, "we don't know yet — ask again"),
 *   - and an incomplete/unresolved instrument (State B, "we can't classify this yet").
 * Plus the pre-fetch-resolve LOADING window every render site must also honor (never render any
 * of the above three before the manifest has settled at least once).
 *
 * Precedence (first match wins):
 *   1. `!manifestLoaded`                                       -> 'LOADING'
 *   2. `instrumentClass === UNKNOWN_CLASS_KEY`                  -> 'UNKNOWN_INSTRUMENT'
 *   3. (broker, class) block absent AND `brokerStatus==='error'` -> 'LOAD_FAILED'
 *   4. (broker, class) block absent, `brokerStatus` otherwise   -> 'UNSUPPORTED'
 *   5. else                                                     -> 'OK'
 *
 * Pure — callers pass in the per-account `byClass` slice (`manifestByBrokerAndClass[broker_id]`,
 * NOT the whole multi-broker map) and that account's `brokerStatus` (`status[broker_id]` from
 * `fetchCapabilitiesForAccounts`'s B3 envelope).
 *
 * @param {{manifestLoaded: boolean, instrumentClass: string, brokerStatus?: ('ok'|'error'|undefined), byClass: (Object|null|undefined)}} args
 * @returns {'LOADING'|'UNKNOWN_INSTRUMENT'|'LOAD_FAILED'|'UNSUPPORTED'|'OK'}
 */
export const resolveAccountGateState = ({ manifestLoaded, instrumentClass, brokerStatus, byClass }) => {
  if (!manifestLoaded) return 'LOADING';
  if (instrumentClass === UNKNOWN_CLASS_KEY) return 'UNKNOWN_INSTRUMENT';
  const blockPresent = !!(byClass && byClass[instrumentClass]);
  if (!blockPresent) {
    return brokerStatus === 'error' ? 'LOAD_FAILED' : 'UNSUPPORTED';
  }
  return 'OK';
};
